import React from 'react';

/**
 * Единый набор линейных иконок интерфейса.
 *
 * Иконки нарисованы inline-SVG и наследуют цвет через `currentColor`,
 * поэтому корректно работают в светлой и тёмной теме и не требуют
 * дополнительных сетевых запросов.
 */

export type IconProps = React.SVGProps<SVGSVGElement> & {
  size?: number;
};

const base = (props: IconProps) => {
  const { size = 20, ...rest } = props;
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
    focusable: false,
    ...rest,
  };
};

export const IconDashboard: React.FC<IconProps> = (props) => (
  <svg {...base(props)}>
    <rect x="3" y="3" width="7.5" height="8.5" rx="2" />
    <rect x="13.5" y="3" width="7.5" height="5.5" rx="2" />
    <rect x="13.5" y="11.5" width="7.5" height="9.5" rx="2" />
    <rect x="3" y="14.5" width="7.5" height="6.5" rx="2" />
  </svg>
);

export const IconAreas: React.FC<IconProps> = (props) => (
  <svg {...base(props)}>
    <path d="M4 6.5 9.5 4l5 2.5L20 4v13.5L14.5 20l-5-2.5L4 20z" />
    <path d="M9.5 4v13.5M14.5 6.5V20" />
  </svg>
);

export const IconCamera: React.FC<IconProps> = (props) => (
  <svg {...base(props)}>
    <path d="M3 8.5a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <path d="M16 11.5 21 9v6l-5-2.5z" />
  </svg>
);

export const IconWallet: React.FC<IconProps> = (props) => (
  <svg {...base(props)}>
    <path d="M3 8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <path d="M3 8V6.8A1.8 1.8 0 0 1 4.8 5H16" />
    <path d="M19 11h2v4h-2a2 2 0 0 1 0-4z" />
  </svg>
);

export const IconUser: React.FC<IconProps> = (props) => (
  <svg {...base(props)}>
    <circle cx="12" cy="8" r="3.75" />
    <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
  </svg>
);

export const IconShield: React.FC<IconProps> = (props) => (
  <svg {...base(props)}>
    <path d="M12 3 19.5 6v5.5c0 4.6-3.1 8.2-7.5 9.5-4.4-1.3-7.5-4.9-7.5-9.5V6z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

export const IconChart: React.FC<IconProps> = (props) => (
  <svg {...base(props)}>
    <path d="M4 20V4" />
    <path d="M4 20h16" />
    <path d="M8.5 16.5v-4" />
    <path d="M13 16.5V8" />
    <path d="M17.5 16.5v-6" />
  </svg>
);

export const IconBell: React.FC<IconProps> = (props) => (
  <svg {...base(props)}>
    <path d="M6.5 10a5.5 5.5 0 0 1 11 0c0 4 1.5 5.5 1.5 5.5H5S6.5 14 6.5 10z" />
    <path d="M10 19a2 2 0 0 0 4 0" />
  </svg>
);

export const IconSun: React.FC<IconProps> = (props) => (
  <svg {...base(props)}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2.5v2M12 19.5v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M2.5 12h2M19.5 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4" />
  </svg>
);

export const IconMoon: React.FC<IconProps> = (props) => (
  <svg {...base(props)}>
    <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5z" />
  </svg>
);

export const IconLogout: React.FC<IconProps> = (props) => (
  <svg {...base(props)}>
    <path d="M14 4h3.5A2.5 2.5 0 0 1 20 6.5v11a2.5 2.5 0 0 1-2.5 2.5H14" />
    <path d="M10 16.5 5.5 12 10 7.5" />
    <path d="M5.5 12H15" />
  </svg>
);

export const IconChevronDown: React.FC<IconProps> = (props) => (
  <svg {...base(props)}>
    <path d="m6 9.5 6 6 6-6" />
  </svg>
);

export const IconChevronRight: React.FC<IconProps> = (props) => (
  <svg {...base(props)}>
    <path d="m9.5 6 6 6-6 6" />
  </svg>
);

export const IconChevronLeft: React.FC<IconProps> = (props) => (
  <svg {...base(props)}>
    <path d="m14.5 6-6 6 6 6" />
  </svg>
);

export const IconPanelLeft: React.FC<IconProps> = (props) => (
  <svg {...base(props)}>
    <rect x="3.5" y="4" width="17" height="16" rx="2.5" />
    <path d="M9.5 4v16" />
  </svg>
);

export const IconMenu: React.FC<IconProps> = (props) => (
  <svg {...base(props)}>
    <path d="M4 7h16M4 12h16M4 17h16" />
  </svg>
);

export const IconClose: React.FC<IconProps> = (props) => (
  <svg {...base(props)}>
    <path d="M6 6l12 12M18 6 6 18" />
  </svg>
);

export const IconPlus: React.FC<IconProps> = (props) => (
  <svg {...base(props)}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

export const IconSearch: React.FC<IconProps> = (props) => (
  <svg {...base(props)}>
    <circle cx="11" cy="11" r="6.5" />
    <path d="m16 16 4.5 4.5" />
  </svg>
);

export const IconPlate: React.FC<IconProps> = (props) => (
  <svg {...base(props)}>
    <rect x="2.5" y="6.5" width="19" height="11" rx="2" />
    <path d="M6 10.5v3M9.5 10.5v3M13 10.5v3M16.5 10.5v3" />
  </svg>
);

export const IconInbox: React.FC<IconProps> = (props) => (
  <svg {...base(props)}>
    <path d="M3.5 13.5 6 5.5h12l2.5 8" />
    <path d="M3.5 13.5h4l1 2.5h7l1-2.5h4v3.5a2 2 0 0 1-2 2H5.5a2 2 0 0 1-2-2z" />
  </svg>
);

export const IconBook: React.FC<IconProps> = (props) => (
  <svg {...base(props)}>
    <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v17H6.5A2.5 2.5 0 0 0 4 22z" />
    <path d="M4 5.5v14A2.5 2.5 0 0 1 6.5 17H20M8 7h8M8 10.5h8" />
  </svg>
);
